import styled, { css } from "styled-components";
import { rawColors } from "../../../design-system/tokens/colors";
import {
  borderWidth,
  borderStyle,
  borderRadius,
} from "../../../design-system/tokens/borders";
import {
  typographyMixin,
  fontWeights,
} from "../../../design-system/tokens/typography";
import {
  authBrandColors,
  authInk,
  authRadius,
  authSizes,
  authTypography,
} from "../../../styles/authBrandTokens";
import type {
  WorkspaceInitialsBadgeShape,
  WorkspaceInitialsBadgeSize,
  WorkspaceInitialsBadgeTone,
} from "./WorkspaceInitialsBadge.types";

type BadgeStyleProps = {
  $size: WorkspaceInitialsBadgeSize;
  $shape: WorkspaceInitialsBadgeShape;
  $tone: WorkspaceInitialsBadgeTone;
  $fill?: string;
};

type AuthAvatarSize = Exclude<WorkspaceInitialsBadgeSize, "md" | "xs">;
type AuthAvatarTone = Exclude<WorkspaceInitialsBadgeTone, "default" | "categorical">;

/** Auth spec `.mb-avatar--sm` (30, radius 9) and `.mb-avatar` (36, radius 10). */
const SIZE_OVERRIDES: Record<AuthAvatarSize, { box: string; radius: string }> = {
  sm: { box: authSizes.avatarCompact, radius: authRadius.avatarCompact },
  lg: { box: authSizes.avatar, radius: authRadius.avatar },
};

/** Auth spec avatar tints (§4) and the identity row's ink circle. */
const TONE_FILLS: Record<AuthAvatarTone, { fill: string; text: string }> = {
  lilac: { fill: rawColors.primary[100], text: rawColors.primary[500] },
  mist: { fill: authBrandColors.mist, text: authBrandColors.info },
  blush: { fill: authBrandColors.blush, text: authBrandColors.blushInk },
  ink: { fill: authInk[900], text: authBrandColors.white },
};

/** `xs`: 22px box, `md` radius, 700 at 9px. */
const xsOverride = css`
  width: 1.375rem; /* GAP: 22px */
  height: 1.375rem; /* GAP: 22px */
  border-radius: ${borderRadius.md};
  font-size: 0.5625rem; /* GAP: 9px */
  font-weight: ${fontWeights.bold};
`;

const sizeOverride = (size: AuthAvatarSize) => css`
  width: ${SIZE_OVERRIDES[size].box};
  height: ${SIZE_OVERRIDES[size].box};
  border-radius: ${SIZE_OVERRIDES[size].radius};
`;

const categoricalOverride = (fill: string | undefined) => css`
  border: 0;
  background: ${fill ?? rawColors.primary[500]};
  color: ${rawColors.text.white};
`;

/** Spec avatars have no outline and use 700 initials: 11px at 30, 12px at 32 and 36. */
const toneOverride = (tone: AuthAvatarTone, size: WorkspaceInitialsBadgeSize) => css`
  border: 0;
  background: ${TONE_FILLS[tone].fill};
  ${size === "sm" ? authTypography("badge") : typographyMixin("caption", 1)}
  color: ${TONE_FILLS[tone].text};
`;

/**
 * 32×32 purple-tinted chip. Background/border use `structural.{100,200}` —
 * the closest scale to Figma `#EBEBFF` / `#DCDCFB` (flagged in styleMap as a
 * minor hue gap; align with design when a new token is added).
 *
 * The base rules are the original chip, untouched, so every existing caller
 * renders exactly as before; `size`, `tone` and `shape` only append overrides.
 */
export const Badge = styled.div<BadgeStyleProps>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  flex-shrink: 0;
  border-radius: ${borderRadius.md};
  border: ${borderWidth[1]} ${borderStyle.solid} ${rawColors.structural[200]};
  background: ${rawColors.structural[100]};
  /* Figma type "Overline 3" = 10/14 Medium. The code token 'overline.3' is 14px Medium,
     'overline.4' is 10px Regular — neither matches exactly. Use overline-4 sizing and
     override the weight to medium for the correct visual. */
  ${typographyMixin("overline", 4)}
  font-weight: ${fontWeights.medium};
  color: ${rawColors.primary[500]};
  text-transform: uppercase;
  user-select: none;
  ${({ $size }) => $size !== "md" && $size !== "xs" && sizeOverride($size)}
  ${({ $tone, $size, $fill }) =>
    $tone === "categorical"
      ? categoricalOverride($fill)
      : $tone !== "default" && toneOverride($tone, $size)}
  ${({ $size }) => $size === "xs" && xsOverride}
  ${({ $shape }) => $shape === "circle" && `border-radius: ${borderRadius.full};`}
`;
